import { BehaviorSubject } from "rxjs";
import { map, tap, distinctUntilChanged } from "rxjs/operators";
import { geo$, fetchGeo } from "@a365/api";
import { CHAT_PHONES, getSalesNumber } from "../a365-core";
import { currentLanguage$ } from "../utils/language-utils";

export const processedGeo$ = new BehaviorSubject(null);

const setDataStorage = (response) => {
  localStorage.setItem("currency_symbol", response.currency_symbol);
  localStorage.setItem("locale", response.locale);
  localStorage.setItem("country_code", response.country);
  localStorage.setItem("country_name", response.country_name);
  localStorage.setItem("entity_id", response.entity);
  localStorage.setItem("seller_id", response.seller);
  localStorage.setItem("country_id", response.country_id);
};

geo$
  .pipe(
    distinctUntilChanged(
      (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
    ),
    map((geoData) => {
      if (!geoData || Object.keys(geoData).length === 0) {
        return processedGeo$.getValue();
      }

      const prevProcessedData = processedGeo$.getValue();
      const countryCode = geoData.country?.toLowerCase();
      const countryID = geoData.country_id;

      if (!countryCode || !countryID) {
        if (prevProcessedData && prevProcessedData.sales_number) {
          return prevProcessedData;
        }
        return geoData;
      }

      const sales_number = getSalesNumber(
        countryCode,
        countryID,
        geoData.today_day,
        geoData.today_hour
      );

      if (
        !sales_number &&
        prevProcessedData &&
        prevProcessedData.sales_number
      ) {
        return { ...geoData, sales_number: prevProcessedData.sales_number };
      }

      return { ...geoData, sales_number };
    }),
    tap((processedData) => { })
  )
  .subscribe((data) => {
    if (data) {
      if (!data.sales_number) {
        const prevData = processedGeo$.getValue();
        if (prevData && prevData.sales_number) {
          data = { ...data, sales_number: prevData.sales_number };
        } else {
          data = { ...data, sales_number: CHAT_PHONES.chatcenter };
        }
      }
      processedGeo$.next(data);
    }
  });

/**
 * Función para cargar geolocalización inicial
 * @param {boolean} storage - Si true, guarda los datos en localStorage
 * @param {boolean} flag - Flag para la API
 * @param {string} country_code - Código de país específico
 */
export const loadGeo = async (
  storage = false,
  flag = false,
  country_code = null
) => {
  try {
    // Hacer la llamada a la API
    const data = await fetchGeo(flag, country_code);

    // Esperar a que se procese el dato con sales_number
    // El procesamiento ocurre en la suscripción a geo$

    // Para garantizar que tenemos los datos procesados con sales_number
    return new Promise((resolve) => {
      // Dar tiempo para que se procese y emita en processedGeo$
      setTimeout(() => {
        const processedData = processedGeo$.getValue();

        if (storage && processedData) {
          setDataStorage(processedData);
        }

        if (processedData && processedData.sales_number) {
          resolve(processedData);
        } else {
          resolve(data);
        }
      }, 100); // Pequeña espera para asegurar que el procesamiento ha ocurrido
    });
  } catch (error) {
    console.error("Error loading geo data:", error);
    throw error;
  }
};

// Función de compatibilidad con el código existente
export const getGeo = loadGeo;

// Reactive language-geo integration
// Business rule: Portuguese language → Brazil geo parameters
let lastLanguage = null;

currentLanguage$.subscribe((language) => {
  // Skip initial subscription and only react to actual changes
  if (lastLanguage === null) {
    lastLanguage = language;
    return;
  }

  // Only trigger geo call if language actually changed
  if (lastLanguage !== language) {
    const currentLang = localStorage.getItem("lang");
    const isPortuguese = currentLang === "pt" || language === "pt-BR";

    if (isPortuguese) {
      // Make geo call with Brazil parameters
      loadGeo(true, true, "BR").catch((error) => {
        console.error(
          "Error loading Brazil geo data on language change:",
          error
        );
      });
    } else {
      // Make standard geo call for other languages
      loadGeo(true, true, null).catch((error) => {
        console.error(
          "Error loading standard geo data on language change:",
          error
        );
      });
    }

    lastLanguage = language;
  }
});

/**
 * Genera una cadena de consulta a partir de un objeto URLSearchParams.
 * @param {URLSearchParams} searchParams - Los parámetros de búsqueda a procesar.
 * @returns {string} - La cadena de consulta generada.
 */
export const getQueryParams = (searchParams) => {
  return Array.from(searchParams.entries())
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");
};
